<script lang="ts">
  import { userbaseStore } from "../../stores/stores";
  import Card from "../../shared/Card.svelte";
  import Button from "../../shared/Button.svelte";
  import Modal from "../../shared/Modal.svelte";
  import Input from "../../shared/Input.svelte";
  import moment from "moment";
  import { ArticleType, DatabaseName, ActionType } from "../../stores/types";
  import type { Entry, DBInventoryEntry } from "../../stores/types";

  moment.defaultFormat = "DD.MM.YYYY";

  export let dbEntry: DBInventoryEntry, itemId: string;

  let showModal = false;
  let actionType: ActionType = null;
  let dbNameInventory = DatabaseName.INVENTORY.toString();
  let entryPromise = null;
  let logPromise = null;
  let buttonLabel = "";
  let buttonType = "secondary";

  const days = (date1: Date, date2: Date) => {
    return Math.round((Number(date1) - Number(date2)) / (1000 * 60 * 60 * 24));
  };

  let fields = {
    amount: 0,
    lastAdded: moment().format("yyyy-MM-DD"),
    dateUsed: moment().format("yyyy-MM-DD"),
    bestBefore: moment().format("yyyy-MM-DD"),
  };

  let sauerteig: boolean = dbEntry.item.article.item.type === "Sauerteig";
  const today = new Date();
  let inventoryItem = dbEntry.item;

  $: freshnessAlert = days(new Date(inventoryItem.bestBefore), today) < 60;
  $: freshnessAlert2 = days(today, new Date(inventoryItem.lastAdded)) > 90;
  $: freshnessAlert3 = days(today, inventoryItem.lastUsed) > 30;
  $: showBestBefore = actionType == ActionType.ADD || actionType == ActionType.ADJUST;
  $: showLastAdded = actionType == ActionType.ADD || actionType == ActionType.ADJUST;
  $: showLastUsed = actionType == ActionType.USE || actionType == ActionType.ADJUST;

  $: amountAlert = inventoryItem.amount < 300 && inventoryItem.article.item.type === ArticleType.MEHL;

  //
  const toggleModal = () => {
    showModal = !showModal;
  };

  // add item
  const handleAdd = () => {
    actionType = ActionType.ADD;
    buttonType = "secondary";
    buttonLabel = "Zufluss eintragen";
    showModal = true;
  };

  // use item
  const handleUse = () => {
    actionType = ActionType.USE;
    buttonType = "primary";
    buttonLabel = "Verbrauch eintragen";
    showModal = true;
  };

  // adjust amount
  const handleAdjust = () => {
    actionType = ActionType.ADJUST;
    buttonType = "secondary";
    buttonLabel = "Menge anpassen";
    fields.amount = inventoryItem.amount;
    fields.bestBefore = moment(inventoryItem.bestBefore).format("yyyy-MM-DD");
    fields.lastAdded = moment(inventoryItem.lastAdded).format("yyyy-MM-DD");
    fields.bestBefore = moment(inventoryItem.bestBefore).format("yyyy-MM-DD");

    showModal = true;
  };

  // delete entry
  const handleDelete = () => {
    if (confirm("really delete this item?:   ")) {
      entryPromise = $userbaseStore.deleteItem({
        databaseName: dbNameInventory,
        itemId,
      });
    }
    logAction();
  };

  // update entry
  const submitHandler = () => {
    if (fields.amount > 0) {
      switch (actionType) {
        case ActionType.ADD:
          inventoryItem.amount = Number(inventoryItem.amount) + Number(fields.amount);
          inventoryItem.lastAdded = new Date(fields.lastAdded);
          break;
        case ActionType.USE:
          inventoryItem.amount = inventoryItem.amount - fields.amount;
          inventoryItem.lastUsed = new Date(fields.dateUsed);
          break;
        case ActionType.ADJUST:
          inventoryItem.amount = fields.amount;
          inventoryItem.lastAdded = new Date(fields.lastAdded);
          inventoryItem.lastUsed = new Date(fields.dateUsed);
          break;
      }

      entryPromise = $userbaseStore.updateItem({
        databaseName: "inventory",
        itemId,
        item: inventoryItem,
      });
      logAction();
    } else {
      alert("Bitte Menge angeben");
    }
    showModal = false;
  };

  const logAction = () => {
    var logItem = {
      dbnane: "inventory",
      action: actionType.toString(),
      changedItem: inventoryItem,
    };
    logPromise = $userbaseStore.insertItem({
      databaseName: "logs",
      item: logItem,
    });
  };
</script>

<Modal {showModal} on:click={toggleModal}>
  <form class="form" on:submit|preventDefault={submitHandler}>
    <h3>{inventoryItem.article.item.name}</h3>
    <Input label="Menge" bind:value={fields.amount} type="number" min="10" required />
    {#if showLastAdded}
      <Input label="gekauft am" bind:value={fields.lastAdded} type="date" required />
    {/if}
    {#if showLastUsed}
      <Input label="verwendet am" bind:value={inventoryItem.lastUsed} type="date" />
    {/if}
    {#if showBestBefore}
      <Input label="Haltbar bis" bind:value={inventoryItem.bestBefore} type="date" required />
    {/if}
    <Button type={buttonType}>{buttonLabel}</Button>
  </form>
</Modal>
<Card>
  <div class="header">
    <h3>{inventoryItem.article.item.name}</h3>
    <div>
      <span class="tag" class:sauerteig>{inventoryItem.article.item.type}</span>
      <span class="icon-button"><i class="fas fa-trash-alt" on:click={() => handleDelete()} /></span>
    </div>
  </div>
  <div class="amountbox">
    <span class="value" class:amountAlert>{inventoryItem.amount} g</span>
    <span class="icon-button" style="padding-left: 1em; color:cadetblue;">
      <i class="fas fa-sliders-h" title="Menge anpassen" on:click={() => handleAdjust()} />
    </span>
    <span class="bestBefore" class:freshnessAlert>
      (Best before: {moment(inventoryItem.bestBefore).format()})
    </span>
  </div>
  <div class="date">
    <span class:freshnessAlert2 on:click={() => handleAdd()}>
      Last added: {moment(inventoryItem.lastAdded).format()}
      <span class="icon-button" style="color:green;"><i title="Zufuhr protokollieren" class="fas fa-plus-circle" /></span>
    </span>
    <span class:freshnessAlert3 on:click={() => handleUse()}>
      Last used:
      {#if inventoryItem.lastUsed === undefined}
        <span style="color:green;">Noch nie</span>
      {:else}
        {moment(inventoryItem.lastUsed).format()}
      {/if}
      <span class="icon-button" style="color:red;"><i title="Verbrauch protokollieren" class="fas fa-minus-circle" /></span>
    </span>
  </div>
</Card>

<style>
  .header,
  .amountbox,
  .date {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  h3 {
    margin: 5px;
    color: rgb(109, 55, 33);
  }
  .date {
    position: relative;
    font-size: small;
    padding-left: 5px;
    cursor: pointer;
    margin-top: 5px;
  }

  .amountbox {
    background-color: bisque;
    padding: 5px;
    text-align: center;
  }
  .freshnessAlert,
  .freshnessAlert2,
  .freshnessAlert3,
  .amountAlert {
    color: red;
  }

  .icon-button {
    font-size: medium;
    color: dimgrey;
    margin-right: 10px;
    cursor: pointer;
  }

  .bestBefore {
    font-size: small;
  }
  .value {
    font-size: large;
  }
  .tag {
    border-radius: 6px;
    padding: 5px;
    background-color: lightsalmon;
  }
  .sauerteig {
    background-color: lightgrey !important;
  }
</style>
